import React from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Pencil, Users } from "lucide-react";
import AttendeeCard from "@/components/directory/AttendeeCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "lv_directorio_llave";

/** Accepts a raw key or a full pasted URL containing ?llave=... */
const extractKey = (raw) => {
  const value = (raw || "").trim();
  if (!value) return "";
  try {
    const url = new URL(value);
    return url.searchParams.get("llave") || url.searchParams.get("edit") || "";
  } catch {
    return value;
  }
};

export default function Directorio() {
  const [searchParams] = useSearchParams();
  const [llave, setLlave] = React.useState(() => {
    const fromUrl = searchParams.get("llave") || "";
    if (fromUrl) return fromUrl;
    return localStorage.getItem(LS_KEY) || "";
  });
  const [keyInput, setKeyInput] = React.useState("");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(Boolean(llave));
  const [error, setError] = React.useState("");

  const load = React.useCallback(async (key) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/attendees/directory`, {
        params: { llave: key },
      });
      localStorage.setItem(LS_KEY, key);
      setData(data);
    } catch {
      localStorage.removeItem(LS_KEY);
      setError("Esa llave no abre el directorio. Revisa el enlace que guardaste al registrarte.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (llave) load(llave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeySubmit = (e) => {
    e.preventDefault();
    const key = extractKey(keyInput);
    if (!key) return;
    setLlave(key);
    load(key);
  };

  // ---- Key recovery by email ----
  const [recoverOpen, setRecoverOpen] = React.useState(false);
  const [recoverEmail, setRecoverEmail] = React.useState("");
  const [recoverLoading, setRecoverLoading] = React.useState(false);
  const [recoverSent, setRecoverSent] = React.useState(false);

  const onRecover = async (e) => {
    e.preventDefault();
    setRecoverLoading(true);
    try {
      await axios.post(`${API}/attendees/recover`, { email: recoverEmail });
      setRecoverSent(true);
    } catch {
      toast.error("No hemos podido enviar el email ahora mismo. Inténtalo en unos minutos.");
    } finally {
      setRecoverLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FAF8F3] text-[#3F403A]" data-testid="directorio-page">
      <header className="border-b border-[#3F403A]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <a href="/" aria-label="Lidera con Voz Propia" className="block" data-testid="directorio-logo">
            <img src="/logo-lidera.png" alt="Lidera con Voz Propia" className="h-12 md:h-14 w-auto" />
          </a>
          <p className="overline text-[#3F403A]/60">Acceso privado</p>
        </div>
      </header>

      {/* ---- Gate: ask for the personal key ---- */}
      {!loading && !data && (
        <div className="max-w-xl mx-auto px-6 py-20 md:py-28" data-testid="directorio-gate">
          <p className="overline text-[#63846F] mb-6">Directorio de asistentes</p>
          <h1 className="font-serif text-4xl md:text-5xl leading-[1.05]">
            Esta sala solo se abre
            <span className="italic text-[#63846F]"> con tu llave.</span>
          </h1>
          <p className="mt-6 font-sans text-base leading-relaxed text-[#3F403A]/80">
            Pega aquí tu enlace personal (el que recibiste al presentarte a la
            sala) o tu llave de acceso.
          </p>
          {error && (
            <p
              className="mt-6 border-l-2 border-[#B77A4B] pl-5 font-sans text-sm text-[#3F403A]/85"
              data-testid="directorio-error"
            >
              {error}
            </p>
          )}
          <form onSubmit={onKeySubmit} className="mt-8 flex flex-col sm:flex-row gap-3">
            <input
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Pega tu enlace o llave"
              className="flex-1 bg-transparent border-0 border-b border-[#3F403A]/25 focus:border-[#63846F] focus:outline-none focus:ring-0 py-3 px-0 font-sans text-base placeholder:text-[#3F403A]/35 rounded-none"
              data-testid="llave-input"
            />
            <button
              type="submit"
              data-testid="llave-submit"
              className="inline-flex items-center justify-center gap-3 border border-[#63846F] bg-[#63846F] text-[#FAF8F3] hover:bg-[#4E6D5A] hover:border-[#4E6D5A] transition-colors px-8 py-3.5 uppercase tracking-[0.22em] text-xs font-medium"
            >
              Entrar <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </form>

          {/* ---- Key recovery ---- */}
          <div className="mt-10 border-t border-[#3F403A]/10 pt-8" data-testid="recover-block">
            {recoverSent ? (
              <p
                className="font-sans text-sm text-[#3F403A]/80 leading-relaxed border-l-2 border-[#63846F] pl-5"
                data-testid="recover-success"
              >
                Si ese email está registrado, recibirás tu enlace de acceso en
                unos minutos. Revisa también la carpeta de spam.
              </p>
            ) : !recoverOpen ? (
              <button
                type="button"
                onClick={() => setRecoverOpen(true)}
                className="overline text-[#63846F] hover:underline underline-offset-4"
                data-testid="recover-toggle"
              >
                ¿Perdiste tu llave?
              </button>
            ) : (
              <form onSubmit={onRecover} className="space-y-5" data-testid="recover-form">
                <p className="overline text-[#3F403A]/60">¿Perdiste tu llave?</p>
                <p className="font-sans text-sm text-[#3F403A]/70 leading-relaxed">
                  Escribe el email con el que te registraste y te la reenviamos
                  automáticamente.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    required
                    value={recoverEmail}
                    onChange={(e) => setRecoverEmail(e.target.value)}
                    placeholder="Tu email de registro"
                    className="flex-1 bg-transparent border-0 border-b border-[#3F403A]/25 focus:border-[#63846F] focus:outline-none focus:ring-0 py-3 px-0 font-sans text-base placeholder:text-[#3F403A]/35 rounded-none"
                    data-testid="recover-email"
                  />
                  <button
                    type="submit"
                    disabled={recoverLoading}
                    data-testid="recover-submit"
                    className="inline-flex items-center justify-center gap-3 border border-[#3F403A]/30 text-[#3F403A] hover:border-[#63846F] hover:text-[#63846F] disabled:opacity-50 transition-colors px-8 py-3.5 uppercase tracking-[0.22em] text-xs font-medium"
                  >
                    {recoverLoading ? "Enviando…" : "Reenviar mi llave"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {loading && (
        <p className="max-w-7xl mx-auto px-6 md:px-10 py-24 font-sans text-sm text-[#3F403A]/60" data-testid="directorio-loading">
          Abriendo la sala…
        </p>
      )}

      {/* ---- Directory ---- */}
      {data && (
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 border-b border-[#3F403A]/12 pb-10 md:pb-14">
            <div>
              <p className="overline text-[#63846F] mb-6">Edición Primer Aniversario · 15 de octubre · Sevilla</p>
              <h1 className="font-serif text-4xl md:text-6xl leading-[1.02]" data-testid="directorio-title">
                Quién es quién
                <span className="italic text-[#63846F]"> en esta edición.</span>
              </h1>
              <p className="mt-6 font-sans text-base md:text-lg text-[#3F403A]/80 max-w-2xl">
                <span data-testid="directorio-count">{data.total}</span>{" "}
                {data.total === 1 ? "asistente presentada" : "asistentes presentadas"}.
                Mujeres con las que compartirás sala — léelas, encuéntralas,
                escríbeles.
              </p>
            </div>
            <a
              href={`/registro?edit=${encodeURIComponent(llave)}`}
              data-testid="directorio-edit-btn"
              className="inline-flex items-center gap-3 border border-[#3F403A]/25 text-[#3F403A] hover:border-[#63846F] hover:text-[#63846F] transition-colors px-7 py-3.5 uppercase tracking-[0.22em] text-xs font-medium shrink-0"
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} /> Editar mi ficha
            </a>
          </div>

          <div
            className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            data-testid="directorio-grid"
          >
            {[...data.attendees]
              .sort((a, b) => (a.id === data.me.id ? -1 : b.id === data.me.id ? 1 : 0))
              .map((a) => (
                <AttendeeCard key={a.id} attendee={a} llave={llave} isMe={a.id === data.me.id} />
              ))}
          </div>

          <p className="mt-14 md:mt-20 border-t border-[#3F403A]/12 pt-8 font-sans text-xs italic text-[#3F403A]/55 flex items-center gap-3">
            <Users className="h-4 w-4 shrink-0" strokeWidth={1.5} />
            Directorio privado — solo visible para las asistentes registradas de esta edición.
          </p>
        </div>
      )}
    </main>
  );
}
